import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, ResponsiveContainer, Legend, Area, AreaChart,
} from "recharts";
import { ArrowClockwise, Coin, Users, ListChecks, Certificate, Broadcast, Buildings, FilePdf, FileXls, FileCsv, Archive, DownloadSimple } from "@phosphor-icons/react";

const HEAT_COLORS = [
  { bg: "bg-red-100", text: "text-red-900" },     // <40
  { bg: "bg-orange-100", text: "text-orange-900" }, // 40-55
  { bg: "bg-amber-100", text: "text-amber-900" },   // 55-70
  { bg: "bg-lime-100", text: "text-lime-900" },     // 70-85
  { bg: "bg-emerald-100", text: "text-emerald-900" }, // >=85
];
function heatClass(pct) {
  const c = HEAT_COLORS[heatIndex(pct)];
  return `${c.bg} ${c.text}`;
}
function heatIndex(pct) {
  if (pct < 40) return 0;
  if (pct < 55) return 1;
  if (pct < 70) return 2;
  if (pct < 85) return 3;
  return 4;
}
const DIFFICULTIES = ["easy", "medium", "hard"];

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/analytics/advanced");
      setData(r.data);
      setLastRefreshedAt(new Date());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load analytics");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(() => { setRefreshing(true); load(); }, 20000);
    return () => clearInterval(iv);
  }, [autoRefresh]);
  // Force re-render every 10s so the "Updated X ago" label stays fresh
  const [, forceTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => forceTick((n) => n + 1), 10000);
    return () => clearInterval(iv);
  }, []);

  const isSuper = user?.role === "super_admin";

  // Build subject × difficulty heatmap matrix
  const heatSubjects = useMemo(() => {
    if (!data?.subject_heatmap) return [];
    const subs = Array.from(new Set(data.subject_heatmap.map((h) => h.subject))).sort();
    return subs;
  }, [data]);
  const heatLookup = useMemo(() => {
    const m = new Map();
    (data?.subject_heatmap || []).forEach((h) => m.set(`${h.subject}::${h.difficulty}`, h));
    return m;
  }, [data]);

  if (loading) return (
    <div data-testid="analytics-loading" className="text-sm text-zinc-500 p-10">Loading analytics…</div>
  );
  if (!data) return null;

  const k = data.kpis;

  return (
    <div data-testid="analytics-page">
      <PageHeader
        overline={data.scope === "platform" ? "Platform analytics" : "Institution analytics"}
        title="Advanced analytics"
        subtitle="Real-time KPIs, coin flow, subject performance heatmap, and top performers."
        action={
          <div className="flex items-center gap-3">
            {lastRefreshedAt && (
              <span className="text-[10px] uppercase tracking-wider text-zinc-500" data-testid="analytics-last-refreshed">
                Updated {formatRelative(lastRefreshedAt)}
              </span>
            )}
            <label className="inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="analytics-autorefresh"
              />
              Auto-refresh 20s
            </label>
            <button
              onClick={() => { setRefreshing(true); load(); }}
              disabled={refreshing}
              className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-xs hover:border-zinc-900 disabled:opacity-50"
              data-testid="analytics-refresh"
            >
              <ArrowClockwise size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        }
      />

      {/* LIVE strip */}
      <div className="mb-6 grid grid-cols-3 border border-zinc-900 bg-zinc-900 text-white" data-testid="analytics-live">
        <LiveStat label="In progress" value={data.live.in_progress} accent="text-emerald-300" icon={Broadcast} />
        <LiveStat label="Paused" value={data.live.paused} accent="text-amber-300" />
        <LiveStat label="Violations · 24h" value={data.live.violations_24h} accent="text-rose-300" />
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-0 border border-zinc-200 mb-8" data-testid="analytics-kpis">
        {isSuper && <Kpi label="Institutions" value={k.total_institutions} icon={Buildings} />}
        <Kpi label={isSuper ? "Users" : "Members"} value={k.total_users} icon={Users} />
        <Kpi label="Students" value={k.total_students} icon={Users} />
        <Kpi label="Exams (published)" value={`${k.published_exams}/${k.total_exams}`} icon={ListChecks} />
        <Kpi label="Attempts" value={k.total_attempts} sub={`${k.submitted_attempts} submitted`} />
        <Kpi label="Certificates" value={k.total_certificates} icon={Certificate} />
        <Kpi label="Coins in circulation" value={(k.coins_in_circulation || 0).toLocaleString()} icon={Coin} accent="text-amber-700" />
        <Kpi label="Avg pass rate" value={avgPassRate(data.exam_pass_rates)} sub="published exams" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Panel title="Attempts · last 30 days" testid="analytics-attempts-chart">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.attempts_timeseries}>
              <defs>
                <linearGradient id="attGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#18181b" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#18181b" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="count" stroke="#18181b" fill="url(#attGrad)" strokeWidth={2} name="Attempts" />
              <Line type="monotone" dataKey="submitted" stroke="#059669" strokeWidth={1.5} name="Submitted" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Coin flow · last 30 days" testid="analytics-coin-chart">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.coin_flow_timeseries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => v.toLocaleString()} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="credit_in" fill="#f59e0b" name="Credits in" stackId="a" />
              <Bar dataKey="credit_out" fill="#78716c" name="Credits out" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Heatmap + Top entities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Panel title="Subject × difficulty · avg score %" testid="analytics-heatmap" className="lg:col-span-2">
          {heatSubjects.length === 0 ? (
            <div className="text-xs text-zinc-500 p-4">No submitted attempts yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="analytics-heatmap-table">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Subject</th>
                    {DIFFICULTIES.map((d) => (
                      <th key={d} className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 capitalize">{d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatSubjects.map((subj) => (
                    <tr key={subj} className="border-t border-zinc-100" data-testid={`heat-row-${subj}`}>
                      <td className="px-3 py-2 font-medium">{subj}</td>
                      {DIFFICULTIES.map((d) => {
                        const cell = heatLookup.get(`${subj}::${d}`);
                        if (!cell) return <td key={d} className="px-3 py-2 text-center text-zinc-300">—</td>;
                        return (
                          <td key={d} className="px-2 py-1 text-center">
                            <div className={`inline-block min-w-[60px] px-3 py-1.5 text-xs font-mono font-bold ${heatClass(cell.avg_pct)}`} title={`${cell.count} attempt${cell.count !== 1 ? "s" : ""}`}>
                              {cell.avg_pct}%
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center gap-2 mt-3 pl-3 text-[10px] text-zinc-500">
                <span>Low</span>
                {HEAT_COLORS.map((c, i) => <span key={i} className={`inline-block w-6 h-3 ${c.bg}`} />)}
                <span>High</span>
              </div>
            </div>
          )}
        </Panel>

        <Panel title={isSuper ? "Top institutions" : "Top students"} testid="analytics-top-entities">
          {data.top_entities.length === 0 ? (
            <div className="text-xs text-zinc-500 p-4">No submissions yet.</div>
          ) : (
            <ol className="space-y-2">
              {data.top_entities.map((e, i) => (
                <li key={e.id} className="flex items-center gap-3 border border-zinc-200 px-3 py-2" data-testid={`top-entity-${i}`}>
                  <div className="font-mono text-xs text-zinc-400 w-6">#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.name}</div>
                    {e.email && <div className="text-[10px] text-zinc-500 truncate">{e.email}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono font-bold">{e.avg_pct}%</div>
                    <div className="text-[10px] text-zinc-500">{e.attempts} attempt{e.attempts !== 1 ? "s" : ""}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>

      {/* Exam pass rates */}
      <Panel title="Exam pass rates · top 8" testid="analytics-exam-pass">
        {data.exam_pass_rates.length === 0 ? (
          <div className="text-xs text-zinc-500 p-4">No submissions yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.exam_pass_rates} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
              <YAxis type="category" dataKey="title" tick={{ fontSize: 10 }} width={140} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="pass_rate" fill="#059669" name="Pass rate %" />
              <Bar dataKey="avg_pct" fill="#71717a" name="Avg score %" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <div className="text-[10px] text-zinc-400 mt-4">Generated at {new Date(data.generated_at).toLocaleString()}</div>

      {/* Export panel */}
      <ExportPanel isSuper={isSuper} />
    </div>
  );
}

function ExportPanel({ isSuper }) {
  const [busy, setBusy] = useState(null);
  const download = async (path, label) => {
    setBusy(path);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : path.split("/").pop();
      const blob = new Blob([r.data], { type: r.headers?.["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${label} downloaded`);
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 404 ? "Nothing to export in your scope" : `Failed to download ${label}`);
    } finally { setBusy(null); }
  };

  const btn = (path, label, icon) => (
    <button
      onClick={() => download(path, label)}
      disabled={busy === path}
      className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-xs hover:border-zinc-900 hover:bg-zinc-50 disabled:opacity-40"
      data-testid={`export-${path.split("/").pop()}`}
    >
      {icon}
      <span>{busy === path ? "Downloading…" : label}</span>
    </button>
  );

  return (
    <div className="mt-8 border border-zinc-200" data-testid="export-panel">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
        <DownloadSimple size={14} className="text-zinc-500" />
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Exports</div>
        <div className="text-xs text-zinc-500 ml-2">Scoped to {isSuper ? "the entire platform" : "your institution"}. All submitted attempts + wallet ledger + certificates.</div>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Exam results</div>
          <div className="flex flex-wrap gap-2">
            {btn("/reports/exam-results.csv", "CSV", <FileCsv size={14} weight="fill" />)}
            {btn("/reports/exam-results.xlsx", "Excel", <FileXls size={14} weight="fill" />)}
            {btn("/reports/exam-results.pdf", "PDF", <FilePdf size={14} weight="fill" />)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Wallet ledger</div>
          <div className="flex flex-wrap gap-2">
            {btn("/reports/wallet-ledger.csv", "CSV", <FileCsv size={14} weight="fill" />)}
            {btn("/reports/wallet-ledger.xlsx", "Excel", <FileXls size={14} weight="fill" />)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Certificates bundle</div>
          <div className="flex flex-wrap gap-2">
            {btn("/reports/certificates-bundle.zip", "ZIP (PDFs)", <Archive size={14} weight="fill" />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="p-4 border-r border-b border-zinc-200 last:border-r-0" data-testid={`kpi-${label.toLowerCase().replace(/\s|·|\//g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={12} className="text-zinc-400" />}
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</div>
      </div>
      <div className={`font-display text-2xl font-bold tabular-nums ${accent || "text-zinc-900"}`}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function LiveStat({ label, value, accent, icon: Icon }) {
  return (
    <div className="p-4 border-r border-white/10 last:border-r-0">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={12} className="text-white/60" />}
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-semibold">{label}</div>
      </div>
      <div className={`font-display text-3xl font-bold tabular-nums mt-1 ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

function Panel({ title, testid, className = "", children }) {
  return (
    <div className={`border border-zinc-200 ${className}`} data-testid={testid}>
      <div className="px-4 py-2 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{title}</div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function avgPassRate(rows) {
  if (!rows || rows.length === 0) return "—";
  const s = rows.reduce((a, r) => a + (r.pass_rate || 0), 0);
  return `${Math.round(s / rows.length)}%`;
}

function formatRelative(d) {
  if (!d) return "";
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return d.toLocaleString();
}
